import { useEffect, useState } from "react";
import { Copy, RotateCcw, Radar, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errorText, BACKEND_URL } from "@/lib/api";

const copy = (text) => navigator.clipboard.writeText(text).then(() => toast.success("Copied")).catch(() => toast.error("Copy failed"));

export const DetectionPanel = ({ onChange }) => {
  const [key, setKey] = useState("");
  const [testUa, setTestUa] = useState("Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; ClaudeBot/1.0; +claudebot@anthropic.com)");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/settings/ingest-key").then((r) => setKey(r.data.key)).catch(() => {}); }, []);

  const rotate = async () => {
    const { data } = await api.post("/settings/ingest-key/rotate");
    setKey(data.key);
    toast.success("New key issued — the old one stops working now");
  };

  const testPing = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/detect", { user_agent: testUa, site: "test-ping" }, { headers: { "X-Doorman-Key": key } });
      data.logged ? toast.success(`Detected ${data.identity} · entry #${data.seq}`) : toast.info(data.reason);
      onChange();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const pixel = `<img src="${BACKEND_URL}/api/detect/pixel.gif?k=${key}" width="1" height="1" alt="" style="position:absolute" />`;
  const server = `// Add to your server (Express example) — works even for crawlers that don't run JavaScript
app.use((req, res, next) => {
  fetch("${BACKEND_URL}/api/detect", {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Doorman-Key": "${key}" },
    body: JSON.stringify({ user_agent: req.get("user-agent"), site: req.hostname + req.path,
      details: { method: req.method, path: req.path, host: req.hostname } })
  }).catch(() => {});
  next();
});`;

  return (
    <section className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-5 space-y-6">
        <div className="rounded-md border border-[#222D3D] bg-[#141A24] p-6 space-y-3">
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Site key</p>
          <div className="flex gap-2">
            <code className="flex-1 font-mono text-xs text-emerald-400 bg-[#07090E] border border-[#222D3D] rounded px-3 py-2 break-all" data-testid="snippet-api-key-display">{key}</code>
            <Button size="icon" variant="outline" className="border-[#222D3D] bg-transparent shrink-0" onClick={() => copy(key)} data-testid="snippet-copy-key-btn"><Copy className="h-4 w-4" /></Button>
            <Button size="icon" variant="outline" className="border-[#222D3D] bg-transparent shrink-0" onClick={rotate} data-testid="snippet-rotate-key-btn"><RotateCcw className="h-4 w-4" /></Button>
          </div>
          <p className="text-xs text-slate-500">Only visits from known AI agents (GPTBot, ClaudeBot, PerplexityBot, Google-Extended, CCBot and ~20 more) are logged. Humans are ignored.</p>
        </div>
        <div className="rounded-md border border-[#222D3D] bg-[#141A24] p-6 space-y-3">
          <div className="flex items-center gap-2 text-cyan-300"><Radar className="h-4 w-4" /><span className="font-display font-semibold">Test detection</span></div>
          <Input value={testUa} onChange={(e) => setTestUa(e.target.value)} className="bg-[#07090E] font-mono text-xs" data-testid="snippet-test-ua-input" />
          <Button onClick={testPing} disabled={busy || !key} className="w-full bg-cyan-500 hover:bg-cyan-400 text-[#07090E] font-semibold" data-testid="snippet-test-ping-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send test visit"}
          </Button>
        </div>
      </div>
      <div className="lg:col-span-7 space-y-6">
        <Snippet title="Server snippet (recommended)" code={server} testId="snippet-copy-server-btn" />
        <Snippet title="Pixel tag (simple, catches agents that load images)" code={pixel} testId="snippet-copy-pixel-btn" />
      </div>
    </section>
  );
};

const Snippet = ({ title, code, testId }) => (
  <div className="rounded-md border border-[#222D3D] bg-[#0F141C]">
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#222D3D]">
      <span className="text-sm text-slate-300">{title}</span>
      <Button size="sm" variant="ghost" onClick={() => copy(code)} data-testid={testId}><Copy className="h-3.5 w-3.5 mr-1.5" />Copy</Button>
    </div>
    <pre className="p-4 text-xs font-mono text-slate-300 overflow-x-auto whitespace-pre">{code}</pre>
  </div>
);
